/**
 * THE resolved price source (SP2 spec §4.2).
 *
 * Replaces the `customPrices` trailing parameter, which had a `= {}` default and
 * therefore made a MISSING ARGUMENT indistinguishable from NO OVERRIDES. That
 * ambiguity has produced two live defects: the CCI bundle shortfall (a2470124, a
 * measured $947.32 on a customer-facing PDF) and the config optimizer ranking
 * candidates at catalog prices. There is no default here on purpose — a consumer
 * that omits the resolver gets a TypeError on the first row instead of a quietly
 * wrong price.
 *
 * `isCustom` is not a convenience. Twelve-plus inline `customPrices[X] !==
 * undefined` checks in generateBom were one question asked twelve times, and are
 * what made adding a BOM row a chance to forget the flag. Both halves come from
 * here or the unit is not worth having.
 *
 * The first argument is the PRICE BOOK, not raw productDb — see App.js:467-470:
 * "priceBook is what every pricing consumer must receive; raw productDb is now
 * spec-sheet data only." The parameter is named for the rule so a raw productDb
 * reads wrong at the call site.
 */
import { getProductPrice } from '../utils/configUtilities';
import type { ProductDb } from './priceBookFromEdition';

export interface PriceResolver {
  /** Dollars: the override if one exists, else the book price for this resolver's quarter. */
  of(partNumber: string): number;
  /** Whether this SKU's price came from an override rather than the book. */
  isCustom(partNumber: string): boolean;
}

export function createPriceResolver(
  priceBook: ProductDb, quarter: string, overrides: Record<string, number>,
): PriceResolver {
  // Copied, not captured: a caller that mutates its map after construction must
  // not retroactively change prices a pricing pass has already resolved.
  const frozen: Record<string, number> = { ...overrides };
  return Object.freeze({
    of(partNumber: string): number {
      const override = frozen[partNumber];
      // `!== undefined`, never falsy — 0 is a legitimate override.
      return override !== undefined
        ? override
        : getProductPrice(priceBook, quarter, partNumber);
    },
    isCustom(partNumber: string): boolean {
      return frozen[partNumber] !== undefined;
    },
  });
}
